/**
 * Resolves {variable} placeholders in template body and subject.
 * Unknown variables are left as-is (never blanked).
 */
export function resolveVariables(template, context) {
  const replace = (text) => {
    if (!text) return text;
    return text.replace(/\{(\w+)\}/g, (match, key) => {
      return context[key] !== undefined && context[key] !== null
        ? context[key]
        : match;
    });
  };
  return {
    subject: replace(template.subject),
    body: replace(template.body),
  };
}

/**
 * Returns the best available WhatsApp-compatible number from a record.
 * Priority: whatsappNumber → phone → primaryContactPhone → null
 *
 * Validation: must be 10-digit Indian mobile starting with 6, 7, 8, or 9.
 * Landlines, short numbers, and foreign numbers return null — icon disabled.
 */
export function getWhatsAppNumber(record) {
  if (!record) return null;

  const raw =
    record.whatsappNumber ||
    record.phone ||
    record.primaryContactPhone ||
    null;

  if (!raw) return null;

  const digits = String(raw).replace(/\D/g, '');

  const mobile =
    digits.startsWith('91') && digits.length === 12
      ? digits.slice(2)
      : digits;

  if (mobile.length === 10 && /^[6-9]/.test(mobile)) {
    return mobile;
  }

  return null;
}

/**
 * Returns the source label for display in panel header.
 */
export function getWhatsAppNumberSource(record) {
  if (!record) return null;
  if (record.whatsappNumber && getWhatsAppNumber({ whatsappNumber: record.whatsappNumber })) {
    return 'whatsappNumber';
  }
  if (record.phone && getWhatsAppNumber({ phone: record.phone })) {
    return 'phone';
  }
  if (record.primaryContactPhone && getWhatsAppNumber({ primaryContactPhone: record.primaryContactPhone })) {
    return 'primaryContactPhone';
  }
  return null;
}

/**
 * Formats phone for wa.me URL.
 * Always returns 'https://wa.me/91{10digits}?text={encoded}'
 * NEVER build this URL inline — always use this helper.
 */
export function formatWhatsAppUrl(phone, message) {
  return `https://wa.me/91${phone}?text=${encodeURIComponent(message)}`;
}

/**
 * Formats a date string or ISO datetime for display.
 * Returns empty string for null/undefined.
 */
export function formatDate(dateStr) {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric'
    });
  } catch { return ''; }
}

/**
 * Extracts and formats time from ISO datetime string.
 * Returns empty string for null/undefined.
 */
export function formatTime(dateStr) {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleTimeString('en-IN', {
      hour: '2-digit', minute: '2-digit', hour12: true
    });
  } catch { return ''; }
}
